import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useSmsAuth } from '../hooks/useSmsAuth';
import { useAuth } from '../hooks/useAuth';

export const LoginPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const { sendLink, loading, loadingMessage, error, linkSent, storedPhone } = useSmsAuth();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const validatePhoneNumber = (phone: string): boolean => {
    // Accept Italian phone numbers in various formats
    // - +39 followed by 9-10 digits
    // - 39 followed by 9-10 digits
    // - 3xx followed by 6-7 more digits (Italian mobile)
    const cleaned = phone.replace(/[\s\-()]/g, '');
    const italianMobileRegex = /^(\+?39)?3\d{8,9}$/;
    return italianMobileRegex.test(cleaned);
  };

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const payload = searchParams.get('payload');
    const decodedPhoneNumber = payload ? atob(payload) : null;
    if (decodedPhoneNumber) {
      setPhoneNumber(decodedPhoneNumber);
    }
  }, [searchParams]);

  const formatPhoneNumber = (phone: string): string => {
    // Clean and format for display
    let cleaned = phone.replace(/[\s\-()]/g, '');

    // Add +39 if not present
    if (cleaned.startsWith('3') && cleaned.length >= 9) {
      cleaned = '+39' + cleaned;
    } else if (cleaned.startsWith('39') && !cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }

    return cleaned;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setPhoneError('');

    if (!phoneNumber.trim()) {
      setPhoneError('Numero di telefono obbligatorio');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError('Inserisci un numero di telefono italiano valido (es. 333 1234567)');
      return;
    }

    const formattedPhone = formatPhoneNumber(phoneNumber);

    try {
      await sendLink(formattedPhone);
    } catch (err) {
      // Error is handled by useSmsAuth hook
    }
  };

  if (authLoading) {
    return (
      <div className="page-shell mobile-safe-padding mobile-safe-top flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Caricamento..." />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  if (linkSent) {
    return (
      <div className="page-shell mobile-safe-padding mobile-safe-top min-h-screen">
        <div className="mx-auto flex min-h-[calc(100vh-6rem)] w-full max-w-4xl items-center justify-center py-6">
          <div className="w-full overflow-hidden rounded-[30px] border border-enpal-downriver/10 bg-white shadow-[0_20px_50px_rgba(7,37,67,0.12)]">
            <div className="bg-gradient-to-r from-enpal-downriver to-enpal-dodger-blue px-6 py-6 sm:px-8">
              <img src="/enpal-logo.png" alt="Enpal" className="h-10 w-auto" />
            </div>
            <div className="px-6 py-7 sm:px-8 sm:py-8">
              <h1 className="mb-2 text-3xl font-bold text-enpal-downriver">Controlla il tuo telefono</h1>
              <p className="mb-2 text-base text-enpal-stone-grey">
                Ti abbiamo inviato un messaggio Whatsapp con il link di accesso al numero{" "}
                <strong>{storedPhone || phoneNumber}</strong>
              </p>
              <p className="text-sm leading-6 text-enpal-stone-grey">
                Clicca sul link nel messaggio Whatsapp per accedere. Il link scadrà tra 10 minuti.
              </p>

              <div className="mt-5 rounded-2xl border border-enpal-dodger-blue/20 bg-enpal-dodger-blue/10 p-4">
                <p className="text-xs italic leading-5 text-enpal-downriver">
                  <strong>Suggerimento:</strong> Se non ricevi il messaggio Whatsapp entro un minuto, verifica che il numero
                  sia corretto e riprova.
                </p>
              </div>

              <Button variant="secondary" onClick={() => navigate("/auth/verify-sms")} className="mt-6">
                Ho cliccato il link
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell mobile-safe-padding mobile-safe-top min-h-screen">
      <div className="mx-auto flex min-h-[calc(100vh-6rem)] w-full max-w-5xl items-center justify-center py-6">
        <div className="grid w-full overflow-hidden rounded-[30px] border border-enpal-downriver/10 bg-white shadow-[0_24px_60px_rgba(7,37,67,0.14)] lg:grid-cols-[1.05fr_1fr]">
          <div className="relative overflow-hidden bg-gradient-to-br from-enpal-dodger-blue to-enpal-downriver p-6 text-white sm:p-8">
            <img src="/enpal-logo.png" alt="Enpal" className="h-10 w-auto" />
            <div className="mt-10">
              <h1 className="text-3xl font-bold leading-tight sm:text-4xl">
                Accedi
              </h1>
              <p className="mt-3 max-w-md text-sm leading-7 text-slate-100/95">
                Inserisci il numero di telefono che hai comunicato per ricevere un link di accesso via Whatsapp
              </p>
            </div>
          </div>

          <div className="bg-white px-6 py-7 sm:px-8 sm:py-8">
            <form onSubmit={handleSubmit}>
              <Input
                type="tel"
                label="Numero di telefono"
                placeholder="333 1234567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                error={phoneError}
                disabled={loading}
                autoComplete="tel"
                required
              />

              {loadingMessage && (
                <div className="info-banner mb-4">
                  <p className="text-center">{loadingMessage}</p>
                </div>
              )}

              {error && (
                <div className="error-banner mb-4">
                  <p className="text-center">{error}</p>
                </div>
              )}

              <Button type="submit" disabled={loading} fullWidth>
                {loading ? loadingMessage || "Elaborazione..." : "Invia link di accesso tramite Whatsapp"}
              </Button>
            </form>

            <p className="mt-5 text-xs leading-5 text-slate-500">
              Riceverai un link sicuro su Whatsapp per completare l'accesso in pochi secondi.
            </p>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs leading-5 text-enpal-stone-grey">
                Se non ricevi il messaggio, verifica il numero inserito e riprova.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
